/* global performance */
import MediaStreamContainer from 'app/services/rtc/MediaStreamContainer';
import MediaDevices from 'app/services/rtc/MediaDevices';
import { TrackState } from 'app/services/rtc/DeviceManager';

export enum ScreenSharingUserAction {
  PersistedRejection = 'PersistedRejection'
}

interface IDisplayMediaSourceProperties {
  readonly streamContainer?: MediaStreamContainer;
  readonly userAction?: ScreenSharingUserAction;
}

const HUMAN_REJECTION_TRESHOLD_MS = 300;

class DisplayMediaSource {
  public readonly streamContainer: MediaStreamContainer;
  public readonly userAction?: ScreenSharingUserAction;
  public readonly mirrorPreview: boolean = false;

  public constructor(
    properties?: IDisplayMediaSourceProperties,
  ) {
    const { streamContainer, userAction } = (properties ?? {});

    this.streamContainer = streamContainer || MediaStreamContainer.create(
      DisplayMediaSource.streamFactory,
    );

    this.userAction = userAction;
  }

  public async start() {
    let streamContainer = this.streamContainer;
    let userAction;
    const start = performance.now();

    try {
      streamContainer = await this.streamContainer.start();
    } catch (error) {
      const end = performance.now();
      const reactionTimeMs = end - start;

      if (error.name === 'NotAllowedError' && reactionTimeMs < HUMAN_REJECTION_TRESHOLD_MS) {
        userAction = ScreenSharingUserAction.PersistedRejection;
      }
    }

    return new DisplayMediaSource({
      streamContainer,
      userAction,
    });
  }

  public stop() {
    return this.update({
      streamContainer: this.streamContainer.stopTracks(),
    });
  }

  public async toggle() {
    if (this.streamContainer.trackState !== TrackState.Active) {
      return this.start();
    }

    return this.stop();
  }

  public updateTrackState() {
    return this.update({
      streamContainer: this.streamContainer.updateTrackState(),
    });
  }

  public dismissUserAction() {
    return this.update({
      userAction: undefined,
    });
  }

  public get stream() {
    return this.streamContainer.stream;
  }

  public get trackState() {
    return this.streamContainer.trackState;
  }

  public get currentSettings() {
    return this.streamContainer.currentSettings;
  }

  private update(properties: IDisplayMediaSourceProperties) {
    return new DisplayMediaSource({
      ...this.properties,
      ...properties,
    });
  }

  private get properties() {
    return {
      streamContainer: this.streamContainer,
      userAction: this.userAction,
    };
  }

  private static streamFactory = async () => {
    return MediaDevices.getDisplayMedia({
      audio: MediaDevices.filterDisplayTrackConstraints({
        autoGainControl: { ideal: false },
        noiseSuppression: { ideal: false },
        echoCancellation: { ideal: false },
      }),
      video: MediaDevices.filterDisplayTrackConstraints({
        width: { max: 1280 },
        height: { max: 1280 },
      }),
    });
  };
}

export default DisplayMediaSource;
